<script lang="ts">
  import type { PagesChangelogIndex } from '@/types/generated'
  import { router } from '@inertiajs/svelte'
  import { onMount } from 'svelte'
  import Layout from '../components/Layout.svelte'
  import Meta from '../components/Meta.svelte'
  import Card from '../components/ui/Card.svelte'
  import Calendar from '@lucide/svelte/icons/calendar'
  import Flame from '@lucide/svelte/icons/flame'
  import MapPin from '@lucide/svelte/icons/map-pin'
  import Camera from '@lucide/svelte/icons/camera'
  import Shield from '@lucide/svelte/icons/shield'
  import MessageSquare from '@lucide/svelte/icons/message-square'
  import MessageCircle from '@lucide/svelte/icons/message-circle'
  import List from '@lucide/svelte/icons/list'
  import Users from '@lucide/svelte/icons/users'
  import Moon from '@lucide/svelte/icons/moon'
  import StickyNote from '@lucide/svelte/icons/sticky-note'
  import PartyPopper from '@lucide/svelte/icons/party-popper'
  import Trophy from '@lucide/svelte/icons/trophy'
  import Heart from '@lucide/svelte/icons/heart'
  import Search from '@lucide/svelte/icons/search'
  import Tag from '@lucide/svelte/icons/tag'
  import Send from '@lucide/svelte/icons/send'
  import Headphones from '@lucide/svelte/icons/headphones'
  import Compass from '@lucide/svelte/icons/compass'
  import Palette from '@lucide/svelte/icons/palette'
  import Store from '@lucide/svelte/icons/store'
  import CloudOff from '@lucide/svelte/icons/cloud-off'
  import ChartNoAxesColumnIncreasing from '@lucide/svelte/icons/chart-no-axes-column-increasing'
  import ListRestart from '@lucide/svelte/icons/list-restart'
  import type { Component } from 'svelte'

  let { releases, acknowledgement }: PagesChangelogIndex = $props()

  onMount(() => {
    if (!acknowledgement) return

    router.patch(
      acknowledgement.path,
      {
        through: acknowledgement.through,
        return_to: acknowledgement.return_path,
      },
      { preserveScroll: true, preserveState: true, replace: true }
    )
  })

  const icons: Record<string, Component> = {
    calendar: Calendar,
    flame: Flame,
    'map-pin': MapPin,
    camera: Camera,
    shield: Shield,
    'message-square': MessageSquare,
    'message-circle': MessageCircle,
    list: List,
    users: Users,
    moon: Moon,
    'sticky-note': StickyNote,
    'party-popper': PartyPopper,
    trophy: Trophy,
    heart: Heart,
    search: Search,
    tag: Tag,
    send: Send,
    headphones: Headphones,
    compass: Compass,
    palette: Palette,
    store: Store,
    'cloud-off': CloudOff,
    'chart-no-axes-column-increasing': ChartNoAxesColumnIncreasing,
    'list-restart': ListRestart,
  }

  const accents: Record<string, { icon: string; background: string }> = {
    amber: {
      icon: 'text-amber-600 dark:text-amber-400',
      background: 'bg-amber-100 dark:bg-amber-950',
    },
    blue: {
      icon: 'text-blue-600 dark:text-blue-400',
      background: 'bg-blue-100 dark:bg-blue-950',
    },
    cyan: {
      icon: 'text-cyan-600 dark:text-cyan-400',
      background: 'bg-cyan-100 dark:bg-cyan-950',
    },
    emerald: {
      icon: 'text-emerald-600 dark:text-emerald-400',
      background: 'bg-emerald-100 dark:bg-emerald-950',
    },
    indigo: {
      icon: 'text-indigo-600 dark:text-indigo-400',
      background: 'bg-indigo-100 dark:bg-indigo-950',
    },
    lime: {
      icon: 'text-lime-600 dark:text-lime-400',
      background: 'bg-lime-100 dark:bg-lime-950',
    },
    orange: {
      icon: 'text-orange-600 dark:text-orange-400',
      background: 'bg-orange-100 dark:bg-orange-950',
    },
    pink: {
      icon: 'text-pink-600 dark:text-pink-400',
      background: 'bg-pink-100 dark:bg-pink-950',
    },
    purple: {
      icon: 'text-purple-600 dark:text-purple-400',
      background: 'bg-purple-100 dark:bg-purple-950',
    },
    red: {
      icon: 'text-red-600 dark:text-red-400',
      background: 'bg-red-100 dark:bg-red-950',
    },
    rose: {
      icon: 'text-rose-600 dark:text-rose-400',
      background: 'bg-rose-100 dark:bg-rose-950',
    },
    sky: {
      icon: 'text-sky-600 dark:text-sky-400',
      background: 'bg-sky-100 dark:bg-sky-950',
    },
    slate: {
      icon: 'text-slate-600 dark:text-slate-400',
      background: 'bg-slate-100 dark:bg-slate-950',
    },
    teal: {
      icon: 'text-teal-600 dark:text-teal-400',
      background: 'bg-teal-100 dark:bg-teal-950',
    },
    yellow: {
      icon: 'text-yellow-600 dark:text-yellow-400',
      background: 'bg-yellow-100 dark:bg-yellow-950',
    },
  }

  const defaultAccent = accents.slate
</script>

<Meta />

<Layout showWhatsNewBanner={false}>
  <div class="container mx-auto px-4 py-8 max-w-5xl">
    <div class="mb-8">
      <h1
        class="text-4xl font-bold mb-2 text-neutral-900 dark:text-neutral-100"
      >
        What's New
      </h1>
      <p class="text-neutral-600 dark:text-neutral-400">
        Updates to Reading Now.
      </p>
    </div>

    <div class="space-y-8">
      {#each releases as release (release.sequence)}
        {@const Icon = icons[release.icon] || PartyPopper}
        {@const accent = accents[release.accent] || defaultAccent}
        <Card class="overflow-hidden">
          <div class="p-6">
            <div class="flex items-start gap-4 mb-4">
              <div
                class="shrink-0 w-12 h-12 rounded-lg {accent.background} flex items-center justify-center"
              >
                <Icon class="w-6 h-6 {accent.icon}" />
              </div>
              <div class="flex-1 min-w-0">
                <h2
                  class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-1"
                >
                  {release.version} - {release.title}
                </h2>
                <span
                  class="text-sm text-neutral-500 dark:text-neutral-400 flex items-center gap-2"
                >
                  <Calendar class="w-4 h-4" />
                  {release.date}
                </span>
              </div>
            </div>

            <p class="text-neutral-700 dark:text-neutral-300 leading-relaxed">
              {release.description}
            </p>
          </div>
        </Card>
      {/each}
    </div>
  </div>
</Layout>
